import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LoginRequest, RegisterRequest, SessionInfo } from '@mytag/shared';
import { ApiError, setSessionId, getSessionId } from '@/lib/apiClient';
import { mytagApi } from '@/services/mytagApi';

interface AuthContextValue {
  session: SessionInfo | null;
  /** true pendant la restauration de session au démarrage. */
  initializing: boolean;
  login: (payload: LoginRequest) => Promise<SessionInfo>;
  register: (payload: RegisterRequest) => Promise<SessionInfo>;
  logout: () => Promise<void>;
  isMock: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [initializing, setInitializing] = useState(true);
  const queryClient = useQueryClient();

  // Restauration de session : le sessionId local est validé par le backend.
  useEffect(() => {
    let cancelled = false;

    async function restore(): Promise<void> {
      if (!getSessionId()) {
        setInitializing(false);
        return;
      }
      try {
        const result = await mytagApi.session();
        if (!cancelled) setSession(result.session);
      } catch (error) {
        if (error instanceof ApiError && error.isAuthError) setSessionId(null);
      } finally {
        if (!cancelled) setInitializing(false);
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const applySession = useCallback(
    (next: SessionInfo) => {
      setSessionId(next.sessionId);
      setSession(next);
      queryClient.clear();
      return next;
    },
    [queryClient],
  );

  const login = useCallback(
    async (payload: LoginRequest) => applySession((await mytagApi.login(payload)).session),
    [applySession],
  );

  const register = useCallback(
    async (payload: RegisterRequest) => applySession((await mytagApi.register(payload)).session),
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await mytagApi.logout();
    } catch {
      /* la session est peut-être déjà expirée côté backend */
    }
    setSessionId(null);
    setSession(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ session, initializing, login, register, logout, isMock: session?.mock ?? false }),
    [session, initializing, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans un AuthProvider.');
  return context;
}
